import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaChevronLeft,
  FaChevronRight,
  FaFilter,
  FaSearch,
  FaSyncAlt,
  FaTerminal,
} from "react-icons/fa";
import Header from "../../../components/ManagementHeader";
import { supabase } from "../../../supabaseClient";
import { formatProfileName } from "../../../utils/profileName";

const PAGE_SIZE = 25;
const SENSITIVE_KEY_PATTERN =
  /(password|passwd|secret|token|api.?key|authorization|cookie|session|encrypted|referral.?code)/i;

const ACTION_LABELS = {
  INSERT: "Created",
  UPDATE: "Updated",
  DELETE: "Deleted",
  TRUNCATE: "Cleared table",
  SIGN_UP: "Account created",
  LOGIN: "Signed in",
  LOGOUT: "Signed out",
  PASSWORD_CHANGED: "Password changed",
  EMAIL_CHANGED: "Email changed",
  EMAIL_CONFIRMED: "Email confirmed",
  VIEW: "Viewed",
  SEARCH: "Searched",
  EXPORT: "Exported",
  DOWNLOAD: "Downloaded",
  PRINT: "Printed",
  PAGE_VIEW: "Opened page",
};

function humanise(value) {
  if (!value) return "System";
  return String(value)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatTimestamp(value) {
  if (!value) return "00/00/0000 00:00:00";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Unknown timestamp";
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  })
    .format(date)
    .replace(",", "");
}

function maskSensitiveData(value) {
  if (Array.isArray(value)) return value.map(maskSensitiveData);
  if (!value || typeof value !== "object") return value;
  return Object.fromEntries(
    Object.entries(value).map(([key, item]) => [
      key,
      SENSITIVE_KEY_PATTERN.test(key)
        ? "[REDACTED]"
        : maskSensitiveData(item),
    ]),
  );
}

function changedFields(oldData, newData) {
  if (!oldData || !newData) return [];
  const keys = new Set([...Object.keys(oldData), ...Object.keys(newData)]);
  return [...keys].filter(
    (key) => JSON.stringify(oldData[key]) !== JSON.stringify(newData[key]),
  );
}

function description(log) {
  const generic =
    typeof log.summary === "string" &&
    /^(INSERT|UPDATE|DELETE|TRUNCATE) on /i.test(log.summary);
  if (log.summary && !generic) return log.summary;
  return `${ACTION_LABELS[log.action] || humanise(log.action)} ${humanise(log.table_name)}`;
}

function actionClass(action) {
  if (["INSERT", "SIGN_UP", "EMAIL_CONFIRMED"].includes(action)) {
    return "pguh-console-action--green";
  }
  if (["DELETE", "TRUNCATE"].includes(action)) {
    return "pguh-console-action--red";
  }
  if (["LOGIN", "LOGOUT", "VIEW", "PAGE_VIEW"].includes(action)) {
    return "pguh-console-action--blue";
  }
  if (["PASSWORD_CHANGED", "EMAIL_CHANGED"].includes(action)) {
    return "pguh-console-action--purple";
  }
  if (["UPDATE", "SEARCH"].includes(action)) {
    return "pguh-console-action--yellow";
  }
  return "pguh-console-action--grey";
}

export default function AuditLog({ session, profile }) {
  const [logs, setLogs] = useState([]);
  const [profiles, setProfiles] = useState({});
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [filters, setFilters] = useState({
    search: "",
    action: "",
    table: "",
    dateFrom: "",
    dateTo: "",
  });
  const [appliedFilters, setAppliedFilters] = useState(filters);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const activeFilterCount = Object.values(appliedFilters).filter(Boolean).length;

  const actionOptions = useMemo(() => Object.keys(ACTION_LABELS), []);

  const loadTableOptions = useCallback(async () => {
    const { data, error } = await supabase.rpc("get_audit_table_options");
    if (!error) {
      setTables(
        [...new Set((data || []).map((row) => row.table_name).filter(Boolean))]
          .sort((a, b) => a.localeCompare(b)),
      );
      return;
    }

    const { data: fallback } = await supabase
      .from("audit_logs")
      .select("table_name")
      .not("table_name", "is", null)
      .limit(1000);

    setTables(
      [...new Set((fallback || []).map((row) => row.table_name).filter(Boolean))]
        .sort((a, b) => a.localeCompare(b)),
    );
  }, []);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");

    const from = (page - 1) * PAGE_SIZE;
    const to = from + PAGE_SIZE - 1;

    let query = supabase
      .from("audit_logs")
      .select(
        "id, occurred_at, actor_id, actor_email, actor_role, action, schema_name, table_name, record_id, summary, old_data, new_data, metadata",
        { count: "exact" },
      )
      .order("occurred_at", { ascending: false })
      .range(from, to);

    if (appliedFilters.action) query = query.eq("action", appliedFilters.action);
    if (appliedFilters.table) query = query.eq("table_name", appliedFilters.table);
    if (appliedFilters.dateFrom) {
      query = query.gte(
        "occurred_at",
        new Date(`${appliedFilters.dateFrom}T00:00:00`).toISOString(),
      );
    }
    if (appliedFilters.dateTo) {
      query = query.lte(
        "occurred_at",
        new Date(`${appliedFilters.dateTo}T23:59:59.999`).toISOString(),
      );
    }

    const search = appliedFilters.search
      .trim()
      .replace(/[,%()]/g, " ")
      .replace(/\s+/g, " ");

    if (search) {
      query = query.or(
        `actor_email.ilike.%${search}%,table_name.ilike.%${search}%,record_id.ilike.%${search}%,summary.ilike.%${search}%`,
      );
    }

    const { data, error, count } = await query;

    if (error) {
      console.error("Unable to load audit logs:", error);
      setErrorMessage(
        error.code === "42501"
          ? "Your account does not have permission to read the audit trail."
          : error.message || "The audit trail could not be loaded.",
      );
      setLogs([]);
      setTotalCount(0);
      setLoading(false);
      return;
    }

    const nextLogs = data || [];
    setLogs(nextLogs);
    setTotalCount(count || 0);
    setLastUpdated(new Date());

    const actorIds = [...new Set(nextLogs.map((log) => log.actor_id).filter(Boolean))];
    if (actorIds.length) {
      const { data: actorProfiles } = await supabase
        .from("profiles")
        .select("id, full_name, name_prefix, email, position")
        .in("id", actorIds);
      setProfiles(
        Object.fromEntries((actorProfiles || []).map((item) => [item.id, item])),
      );
    } else {
      setProfiles({});
    }

    setLoading(false);
  }, [appliedFilters, page]);

  useEffect(() => {
    void loadTableOptions();
  }, [loadTableOptions]);

  useEffect(() => {
    void loadLogs();
  }, [loadLogs]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const applyFilters = (event) => {
    event.preventDefault();
    setPage(1);
    setAppliedFilters({ ...filters });
  };

  const clearFilters = () => {
    const empty = { search: "", action: "", table: "", dateFrom: "", dateTo: "" };
    setFilters(empty);
    setAppliedFilters(empty);
    setPage(1);
  };

  const actorFor = (log) => {
    const actor = log.actor_id ? profiles[log.actor_id] : null;
    return {
      name: formatProfileName(actor, actor?.email || log.actor_email || "System process"),
      role: actor?.position || log.actor_role || "Database service",
    };
  };

  const command = useMemo(() => {
    const parts = ["audit:list", `--page=${page}`, `--limit=${PAGE_SIZE}`];
    if (appliedFilters.action) parts.push(`--action=${appliedFilters.action.toLowerCase()}`);
    if (appliedFilters.table) parts.push(`--table=${appliedFilters.table}`);
    if (appliedFilters.search) parts.push(`--search=\"${appliedFilters.search}\"`);
    if (appliedFilters.dateFrom) parts.push(`--from=${appliedFilters.dateFrom}`);
    if (appliedFilters.dateTo) parts.push(`--to=${appliedFilters.dateTo}`);
    return parts.join(" ");
  }, [appliedFilters, page]);

  return (
    <div className="pguh-page-shell">
      <Header session={session} profile={profile} />

      <main className="pguh-main">
        <div className="nhsuk-width-container">
          <div className="pguh-section-heading pguh-audit-console-heading">
            <div>
              <span className="pguh-eyebrow">Management oversight</span>
              <h1 className="nhsuk-heading-xl">Audit console</h1>
              <p className="nhsuk-body-l">
                Monitor account activity and recorded database changes across PGUH services.
              </p>
            </div>
            <button
              type="button"
              className="nhsuk-button nhsuk-button--secondary pguh-audit-console-refresh"
              onClick={() => {
                void loadTableOptions();
                void loadLogs();
              }}
              disabled={loading}
            >
              <FaSyncAlt className={loading ? "pguh-audit-refresh-icon--loading" : ""} />
              {loading ? "Refreshing" : "Refresh console"}
            </button>
          </div>

          <section className="pguh-audit-console" aria-labelledby="audit-console-title">
            <header className="pguh-audit-console__topbar">
              <div className="pguh-audit-console__brand">
                <span className="pguh-audit-console__brand-icon"><FaTerminal /></span>
                <div>
                  <strong id="audit-console-title">PGUH Audit Service</strong>
                  <span>Management activity monitoring</span>
                </div>
              </div>
              <div className="pguh-audit-console__status">
                <span><i className="pguh-audit-console__status-dot" />Online</span>
                <span>{totalCount.toLocaleString("en-GB")} events</span>
                <span>{tables.length.toLocaleString("en-GB")} tables</span>
              </div>
            </header>

            <section className="pguh-audit-console__filters">
              <div className="pguh-audit-console__filter-heading">
                <div>
                  <h2><FaFilter /> Query filters</h2>
                  <p>Narrow output by account, action, service or date.</p>
                </div>
                <span className="pguh-audit-console__filter-count">
                  {activeFilterCount} active filter{activeFilterCount === 1 ? "" : "s"}
                </span>
              </div>

              <form onSubmit={applyFilters}>
                <div className="pguh-audit-console__filter-grid">
                  <div className="nhsuk-form-group pguh-audit-console__search-field">
                    <label className="nhsuk-label" htmlFor="audit-search">Search output</label>
                    <div className="pguh-audit-console__search">
                      <FaSearch />
                      <input
                        id="audit-search"
                        className="nhsuk-input"
                        type="search"
                        value={filters.search}
                        onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))}
                        placeholder="Email, table, record ID or description"
                      />
                    </div>
                  </div>

                  <div className="nhsuk-form-group">
                    <label className="nhsuk-label" htmlFor="audit-action">Action</label>
                    <select
                      id="audit-action"
                      className="nhsuk-select"
                      value={filters.action}
                      onChange={(event) => setFilters((current) => ({ ...current, action: event.target.value }))}
                    >
                      <option value="">All actions</option>
                      {actionOptions.map((action) => (
                        <option key={action} value={action}>{ACTION_LABELS[action]}</option>
                      ))}
                    </select>
                  </div>

                  <div className="nhsuk-form-group">
                    <label className="nhsuk-label" htmlFor="audit-table">Table or service</label>
                    <select
                      id="audit-table"
                      className="nhsuk-select"
                      value={filters.table}
                      onChange={(event) => setFilters((current) => ({ ...current, table: event.target.value }))}
                    >
                      <option value="">All tables</option>
                      {tables.map((table) => <option key={table}>{table}</option>)}
                    </select>
                  </div>

                  <div className="nhsuk-form-group">
                    <label className="nhsuk-label" htmlFor="audit-from">From date</label>
                    <input
                      id="audit-from"
                      className="nhsuk-input"
                      type="date"
                      value={filters.dateFrom}
                      onChange={(event) => setFilters((current) => ({ ...current, dateFrom: event.target.value }))}
                    />
                  </div>

                  <div className="nhsuk-form-group">
                    <label className="nhsuk-label" htmlFor="audit-to">To date</label>
                    <input
                      id="audit-to"
                      className="nhsuk-input"
                      type="date"
                      value={filters.dateTo}
                      onChange={(event) => setFilters((current) => ({ ...current, dateTo: event.target.value }))}
                    />
                  </div>
                </div>

                <div className="pguh-audit-console__filter-actions">
                  <button className="nhsuk-button" type="submit">Run query</button>
                  <button className="nhsuk-button nhsuk-button--secondary" type="button" onClick={clearFilters}>
                    Clear filters
                  </button>
                </div>
              </form>
            </section>

            <div className="pguh-audit-console__command">
              <span className="pguh-audit-console__prompt">management@pguh:~$</span>
              <code>{command}</code>
              <span className="pguh-audit-console__cursor" />
            </div>

            <div className="pguh-audit-console__screen" aria-busy={loading}>
              {errorMessage && (
                <div className="pguh-audit-console__error" role="alert">
                  <span>[ERROR]</span>
                  <strong>Audit service request failed</strong>
                  <p>{errorMessage}</p>
                </div>
              )}

              {loading && (
                <div className="pguh-audit-console__loading" role="status">
                  <span><b className="pguh-audit-console__prompt-symbol">&gt;</b> Connecting to PGUH audit service…</span>
                  <span><b className="pguh-audit-console__prompt-symbol">&gt;</b> Executing query…</span>
                </div>
              )}

              {!loading && !errorMessage && logs.length === 0 && (
                <div className="pguh-audit-console__empty">
                  <p><span>[RESULT]</span> No events matched the current filters.</p>
                </div>
              )}

              {!loading && logs.length > 0 && (
                <div className="pguh-audit-console__output" role="log">
                  {logs.map((log, index) => {
                    const actor = actorFor(log);
                    const fields = changedFields(log.old_data, log.new_data);
                    const oldData = maskSensitiveData(log.old_data);
                    const newData = maskSensitiveData(log.new_data);
                    const metadata = maskSensitiveData(log.metadata);
                    const hasMetadata = metadata && typeof metadata === "object" && Object.keys(metadata).length;

                    return (
                      <article className="pguh-audit-console-line" key={log.id}>
                        <div className="pguh-audit-console-line__primary">
                          <span className="pguh-audit-console-line__number">
                            {String(log.id ?? index + 1).padStart(6, "0")}
                          </span>
                          <time className="pguh-audit-console-line__time" dateTime={log.occurred_at}>
                            [{formatTimestamp(log.occurred_at)}]
                          </time>
                          <span className={`pguh-audit-console-line__action ${actionClass(log.action)}`}>
                            {log.action || "SYSTEM"}
                          </span>
                          <strong className="pguh-audit-console-line__message">{description(log)}</strong>
                        </div>

                        <div className="pguh-audit-console-line__secondary">
                          <span className="pguh-audit-console-line__marker">└─</span>
                          <span className="pguh-audit-console-line__actor">actor=<strong>{actor.name}</strong></span>
                          <span>role=<strong>{actor.role}</strong></span>
                          <span>target=<strong>{log.schema_name || "public"}.{log.table_name || "system"}</strong></span>
                          <span>record=<strong>{log.record_id || "not-applicable"}</strong></span>
                        </div>

                        {(fields.length > 0 || oldData || newData || hasMetadata) && (
                          <details className="pguh-audit-console-line__details">
                            <summary><span>&gt;</span> inspect event payload</summary>
                            <div className="pguh-audit-console-line__payload">
                              {fields.length > 0 && (
                                <p className="pguh-audit-console-line__changed">
                                  <strong>Changed fields:</strong> {fields.map(humanise).join(", ")}
                                </p>
                              )}
                              <div className="pguh-audit-console-json-grid">
                                {oldData && <section><h4>Before</h4><pre>{JSON.stringify(oldData, null, 2)}</pre></section>}
                                {newData && <section><h4>After</h4><pre>{JSON.stringify(newData, null, 2)}</pre></section>}
                                {hasMetadata && <section><h4>Metadata</h4><pre>{JSON.stringify(metadata, null, 2)}</pre></section>}
                              </div>
                            </div>
                          </details>
                        )}
                      </article>
                    );
                  })}
                </div>
              )}
            </div>

            <footer className="pguh-audit-console__footer">
              <div className="pguh-audit-console__footer-status">
                <span>STATUS <strong>{errorMessage ? "ERROR" : loading ? "RUNNING" : "READY"}</strong></span>
                <span>RESULTS <strong>{totalCount.toLocaleString("en-GB")}</strong></span>
                <span>PAGE <strong>{page}/{totalPages}</strong></span>
                <span>UPDATED <strong>{lastUpdated ? lastUpdated.toLocaleTimeString("en-GB") : "—"}</strong></span>
              </div>

              {totalCount > 0 && (
                <nav className="pguh-audit-console__pagination" aria-label="Audit pages">
                  <button type="button" disabled={page <= 1 || loading} onClick={() => setPage((current) => Math.max(1, current - 1))}>
                    <FaChevronLeft /> Previous
                  </button>
                  <span>Page <strong>{page}</strong> of <strong>{totalPages}</strong></span>
                  <button type="button" disabled={page >= totalPages || loading} onClick={() => setPage((current) => Math.min(totalPages, current + 1))}>
                    Next <FaChevronRight />
                  </button>
                </nav>
              )}
            </footer>
          </section>
        </div>
      </main>
    </div>
  );
}
